import { AxiosResponse } from 'axios';
import { useMutation, useQuery } from 'react-query';
import apiClient from '../apiClient/apiClient';
import {
  ClassSize, Size, CreateClassSizeRequest,
  ClassSizeKey, SizeKey, UpdateSizeRequest
} from './size.types';

export const useGetSizes = () => {
  return useQuery({
    queryKey: ['getSizes'],
    queryFn: () =>
        apiClient.get<Size[]>('/sizes')
  });
};

export const useGetSize = (
    {
      sizeId
    }: SizeKey
) => {
  return useQuery({
    queryKey: ['getSize', sizeId],
    queryFn: () =>
        apiClient.get<Size>(`/sizes/${sizeId}`)
  });
};

export const useUpdateSize = (
    {
        sizeId
    }: SizeKey
) => {
  return useMutation({
    mutationKey: ['updateSize', sizeId],
    mutationFn: (request: UpdateSizeRequest) =>
        apiClient.put<Size>(`/sizes/${sizeId}`, request)
  });
};

export const useDeleteSize = (
    {
      sizeId
    }: SizeKey
) => {
  return useMutation({
    mutationKey: ['deleteSize', sizeId],
    mutationFn: () =>
        apiClient.delete(`/sizes/${sizeId}`)
  })
}

export const useGetClassSizes = (
  requestBody: {
    classNo?: string;
    sizeNo?: string;
    status?: number
  },
  enabled: boolean = true
) => {
  return useQuery<AxiosResponse<ClassSize[]>>({
    queryKey: ['sizes', requestBody.classNo, requestBody.sizeNo, requestBody.status],
    queryFn: () => apiClient.get<ClassSize[]>('/class-sizes', { params: requestBody }),
    enabled
  });
};

export const useCreateClassSize = () => {
  return useMutation({
    mutationKey: ['createClassSize'],
    mutationFn: ( request: CreateClassSizeRequest ) => {
      return apiClient.post<ClassSize>(`/classes/${request.classId}/sizes`, request.body );
    }
  });
};

export const useDeleteClassSize = () => {
  return useMutation({
    mutationKey: ['deleteClassSize'],
    mutationFn: (request: ClassSizeKey) => {
      return apiClient.delete(`/classes/${request.classId}/sizes/${request.sizeId}`);
    }
  });
};

export const useGetRecipeSizes = (
    requestBody: { classNo: string; baseNo: string},
    enabled: boolean = false
) => {
  return useQuery<AxiosResponse<Size[]>>({
    queryKey: ['getRecipeSizes', requestBody.classNo, requestBody.baseNo],
    queryFn: () => apiClient.get<Size[]>('/recipes/sizes', { params: requestBody }),
    enabled
  });
};

export const useGetSubRecipeSizes = (
    requestBody: { classNo: string; baseNo?: string },
    enabled: boolean = false
) => {
  return useQuery<AxiosResponse<Size[]>>({
    queryKey: ['getSubRecipeSizes', requestBody.classNo, requestBody.baseNo],
    queryFn: () => apiClient.get<Size[]>('/sub-recipes/sizes', { params: requestBody }),
    enabled
  });
};

export const useGetMenuItemRecipeSizes = (
  menuNo: string,
  requestBody: { classNo?: number; baseNo?: number; strategyNo?: string },
  enabled: boolean = false
) => {
  return useQuery<AxiosResponse<Size[]>>({
    queryKey: ['getMenuItemRecipeSizes', menuNo, requestBody.classNo, requestBody.baseNo, requestBody.strategyNo],
    queryFn: () => apiClient.get<Size[]>(`/menus/${menuNo}/items/recipes/sizes`, { params: requestBody }),
    enabled
  });
};

export const useGetMenuItemSubRecipeSizes = (
  menuNo: string,
  requestBody: { classNo?: number; baseNo?: number },
  enabled: boolean = false
) => {
  return useQuery<AxiosResponse<Size[]>>({
    queryKey: ['getMenuItemSubRecipeSizes', menuNo, requestBody.classNo, requestBody.baseNo],
    queryFn: () => apiClient.get<Size[]>(`/menus/${menuNo}/items/sub-recipes/sizes`, { params: requestBody }),
    enabled
  });
};
